import { formatDate } from "@creators/backend/lib/time";

export type SystemEventKind =
  | "offer"
  | "counter"
  | "accepted"
  | "payment"
  | "milestone"
  | "draft"
  | "revision"
  | "published"
  | "cancelled"
  | "info";

export type SystemEventTone = "brand" | "creator" | "money" | "warn" | "neutral";

export type ParsedSystemEvent = {
  kind: SystemEventKind;
  title: string;
  detail: string | null;
  tone: SystemEventTone;
};

/**
 * Details read as fragments in the timeline, so they carry no trailing stop,
 * and raw ISO dates from the backend get rendered the way the rest of the UI
 * shows dates.
 */
function tidyDetail(
  text: string | null | undefined,
  names?: { creator: string; brand: string },
): string | null {
  if (!text) return null;
  let cleaned = text.replace(/(\d{4}-\d{2}-\d{2})/g, (iso) => formatDate(iso));

  if (names) {
    cleaned = cleaned
      .replace(/\bthe (?:brand|collaborator)\b/gi, names.brand)
      .replace(/\bthe creator\b/gi, names.creator);
  }

  return cleaned.trim().replace(/\s*\.$/, "") || null;
}

function formatAmountChange(text: string): string | null {
  const match = text.match(/amount\s+(₹[\d,]+)\s*→\s*(₹[\d,]+)/i);
  if (match) return `${match[1]} → ${match[2]}`;
  return text.trim() || null;
}

function expiryDetail(body: string): string | null {
  const match = body.match(/expires in (\d+ days?)/i);
  return match ? `Expires in ${match[1]}` : null;
}

/** Strip legacy version labels from older system messages. */
export function normalizeSystemBody(body: string): string {
  return body
    .replace(/\bcountered with v\d+[:\s—-]*/gi, "countered — ")
    .replace(/\baccepted v\d+\.\s*/gi, "accepted the terms. ")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function actorName(body: string, names: { creator: string; brand: string }): string {
  if (/^The (?:brand|collaborator)\b/i.test(body)) return names.brand;
  if (/^The creator\b/i.test(body)) return names.creator;
  return "Platform";
}

function stripExpiryTail(text: string): string {
  return text.replace(/\s*Offer expires in \d+ days\.?\s*$/i, "").trim();
}

function firstSentence(text: string): [string, string | null] {
  const match = text.match(/^(.+?[.!?])(?:\s+([\s\S]*))?$/);
  if (!match) return [text, null];
  const rest = match[2]?.trim();
  return [match[1], rest || null];
}

/** Drop legacy "Serious:" / "Priority:" prefixes — tier is shown as an icon. */
function stripTierPrefix(text: string): string {
  return text.replace(/^(Serious|Priority)\s*:\s*/i, "").trim();
}

export function parseSystemEvent(
  rawBody: string,
  names: { creator: string; brand: string },
): ParsedSystemEvent {
  const body = stripTierPrefix(normalizeSystemBody(rawBody));

  if (/\bsent an offer\b|\bsent a barter offer\b/i.test(body)) {
    const who = stripTierPrefix(body.match(/^(.+?) sent /i)?.[1]?.trim() ?? names.brand);
    return {
      kind: "offer",
      title: `${who} sent an offer`,
      detail: expiryDetail(body),
      tone: "brand",
    };
  }

  if (/\bcountered\b/i.test(body)) {
    const who = actorName(body, names);
    let rest = body
      .replace(/^The (brand|collaborator|creator)\s+countered\s*[—:-]\s*/i, "")
      .replace(/^The (brand|collaborator|creator)\s+countered\s*/i, "");
    rest = stripExpiryTail(rest);
    return {
      kind: "counter",
      title: `${who} countered`,
      detail: formatAmountChange(rest) ?? (rest || null),
      tone: "neutral",
    };
  }

  if (/\baccepted the terms\b|\baccepted\b/i.test(body) && /frozen|Work can begin/i.test(body)) {
    const who = actorName(body, names);
    const amount = body.match(/frozen at (₹[\d,]+)/i)?.[1];
    const window = body.match(/has (\d+ days?) to send/i)?.[1];
    const advance = body.match(/the (₹[\d,]+) advance/i)?.[1];
    const parts = [
      amount ? `${amount} locked in` : null,
      advance ? `${advance} advance` : null,
      window ? `send within ${window}` : null,
    ].filter(Boolean);
    return {
      kind: "accepted",
      title: `${who} accepted`,
      detail: parts.length > 0 ? parts.join(" · ") : null,
      tone: "neutral",
    };
  }

  if (/marked as sent/i.test(body)) {
    const amount = body.match(/(₹[\d,]+)/)?.[1];
    const method = body.match(/via ([^—.]+)/i)?.[1]?.trim();
    const parts = [amount ? `${amount} sent` : null, method ? `via ${method}` : null].filter(
      Boolean,
    );
    return {
      kind: "payment",
      title: `Awaiting ${names.creator}'s confirmation`,
      detail: parts.length > 0 ? parts.join(" · ") : null,
      tone: "neutral",
    };
  }

  if (/confirmed received/i.test(body)) {
    const amount = body.match(/(₹[\d,]+)/)?.[1];
    return {
      kind: "payment",
      title: "Payment confirmed",
      detail: amount ? `${amount} landed in ${names.creator}'s account` : null,
      tone: "money",
    };
  }

  if (/not received/i.test(body)) {
    const amount = body.match(/(₹[\d,]+)/)?.[1];
    const reason = body.split("—")[1]?.trim();
    return {
      kind: "payment",
      title: `${names.creator} has not received this`,
      detail: tidyDetail([amount, reason].filter(Boolean).join(" · ")),
      tone: "warn",
    };
  }

  if (/advance received/i.test(body)) {
    return {
      kind: "milestone",
      title: "Advance received",
      detail: `${names.creator} can start work`,
      tone: "money",
    };
  }

  if (/is now due/i.test(body)) {
    const amount = body.match(/(₹[\d,]+)/)?.[1];
    const trigger = body.match(/\(([^)]+)\)/)?.[1];
    return {
      kind: "milestone",
      title: "Payment due",
      detail: [amount, trigger].filter(Boolean).join(" · ") || null,
      tone: "money",
    };
  }

  if (/draft is due by/i.test(body)) {
    const due = body.match(/due by ([^.]+)/i)?.[1];
    return {
      kind: "draft",
      title: "Draft deadline",
      detail: tidyDetail(due ? `Deliver by ${due}` : body),
      tone: "creator",
    };
  }

  if (/submitted a draft/i.test(body)) {
    const days = body.match(/has (\d+ days?) to review/i)?.[1];
    return {
      kind: "draft",
      title: `${names.creator} submitted draft`,
      detail: days ? `${names.brand} has ${days} to review` : null,
      tone: "neutral",
    };
  }

  if (/requested (a )?revision/i.test(body)) {
    const modern = body.match(/requested a revision:\s*(.+)/i);
    if (modern) {
      return {
        kind: "revision",
        title: `${names.brand} requested a revision`,
        detail: tidyDetail(modern[1]),
        tone: "neutral",
      };
    }
    const legacy = body.match(/requested revision (\d+) of (\d+):\s*(.+)/i);
    if (legacy) {
      return {
        kind: "revision",
        title: `${names.brand} requested a revision`,
        detail: tidyDetail(legacy[3]),
        tone: "neutral",
      };
    }
    return {
      kind: "revision",
      title: `${names.brand} requested a revision`,
      detail: null,
      tone: "neutral",
    };
  }

  if (/approved the draft|draft.*approved|auto-approv/i.test(body)) {
    const [, detail] = firstSentence(body);
    return {
      kind: "draft",
      title: "Draft approved",
      detail: tidyDetail(detail, names),
      tone: "brand",
    };
  }

  if (/live period ended|every payment is confirmed/i.test(body)) {
    return {
      kind: "milestone",
      title: "Collab complete",
      detail: "Live period ended and every payment confirmed",
      tone: "money",
    };
  }

  if (/submitted.*live|published url|post is live|confirmed.*live/i.test(body)) {
    const title = /confirmed/i.test(body) ? "Post confirmed live" : "Live link shared";
    const [, detail] = firstSentence(body);
    return { kind: "published", title, detail: tidyDetail(detail, names), tone: "creator" };
  }

  if (/^call cancelled/i.test(body)) {
    return {
      kind: "info",
      title: "Call cancelled",
      detail: null,
      tone: "warn",
    };
  }

  if (/cancelled/i.test(body)) {
    return {
      kind: "cancelled",
      title: "Collab cancelled",
      detail: tidyDetail(body, names),
      tone: "warn",
    };
  }

  if (/asked to talk on a call|call requested/i.test(body)) {
    return {
      kind: "info",
      title: "Call requested",
      detail: tidyDetail(body, names),
      tone: "neutral",
    };
  }

  if (/boost paid|fee paid/i.test(body)) {
    const amount = body.match(/(₹[\d,.]+(?:\.\d+)?)/)?.[1];
    return {
      kind: "info",
      title: "Offer fee paid",
      detail: amount ? `${amount} incl. GST · non-refundable` : "Non-refundable",
      tone: "money",
    };
  }

  if (/calls are on|Call option is on|Priority offer —/i.test(body)) {
    return {
      kind: "info",
      title: "Call option enabled",
      detail: tidyDetail(
        body
          .replace(/^Calls are on\.\s*/i, "")
          .replace(/^Priority offer — Call option is on for this collab\.\s*/i, "")
          .replace(/^Priority offer — calls are on for this collab\.\s*/i, "")
          .trim() || null,
        names,
      ),
      tone: "brand",
    };
  }

  if (/calling the collaborator|connecting call/i.test(body)) {
    return {
      kind: "info",
      title: "Calling…",
      detail: tidyDetail(body, names),
      tone: "neutral",
    };
  }

  if (
    /call ended|call recording|call did not connect|call not answered|line was busy/i.test(body)
  ) {
    const isRecording = /recording/i.test(body);
    const isEnded = /call ended/i.test(body);
    // Avoid "Call recording · Call recording (1 min)" — keep only the duration bit.
    const paren = body.match(/\(([^)]+)\)/)?.[1]?.trim() ?? null;
    return {
      kind: "info",
      title: isRecording ? "Call recording" : isEnded ? "Call ended" : "Call update",
      detail: isRecording
        ? paren
        : isEnded
          ? paren
            ? paren
            : tidyDetail(body.replace(/^Call ended\.?\s*/i, "").trim() || null, names)
          : tidyDetail(body, names),
      tone: /did not connect|not answered|busy/i.test(body) ? "warn" : "money",
    };
  }

  if (/expires in/i.test(body)) {
    return {
      kind: "info",
      title: "Offer expiring",
      detail: tidyDetail(body, names),
      tone: "neutral",
    };
  }

  const [title, detail] = firstSentence(body);
  const long = title.length > 72;
  return {
    kind: "info",
    title: long ? "Update" : title.replace(/\s*\.$/, ""),
    detail: tidyDetail(long ? body : detail, names),
    tone: "neutral",
  };
}
